import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '@/components/Ad/AuthorInfo/AuthorInfo.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { PhoneNumbers } from '@/components/PhoneNumbers/PhoneNumbers';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import {
  buildWorkingScheduleString,
  getUsersAdsHref,
} from '@/components/Ad/AuthorInfo/AuthorInfo.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { AdStatus } from '@/types';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

export const AuthorInfo = () => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const isAuthor = useIsAuthor();
  const { categoryDetails } = useCurrentCategoryDetails();

  if (!adDetails) {
    return (
      <>
        <SkeletonLoader
          dataTestId="author-info-skeleton-loader"
          borderRadius={7}
          height="131px"
        />
        <Divider $isSmall />
      </>
    );
  }

  const { shop, contact, id, status, user } = adDetails;

  const isInfoVisible = isAuthor || status === AdStatus.Published;

  const isWarningVisible =
    !isAuthor &&
    !shop &&
    (status === AdStatus.Published || status === AdStatus.Expired);

  const info = {
    name: shop ? shop.name : contact.name,
    avatar: shop ? shop.logo : contact.logo?.url,
    phones: contact.phones,
  };

  const usersAdsHref =
    !shop && user?.publishedAdsCount > 1 && getUsersAdsHref(user.legacyId);

  return (
    <>
      {isInfoVisible && (
        <>
          <S.Main
            {...(shop?.uri
              ? {
                  href: shop.uri,
                  as: CustomLink,
                  dataTestId: 'author-info-main',
                }
              : { as: 'div', 'data-testid': 'author-info-main' })}
          >
            <div>
              <Typography.Body $variant="1" data-testid="author-info-name">
                {info.name}
              </Typography.Body>
              {!!shop && (
                <S.WorkingHours data-testid="author-info-working-schedule">
                  {buildWorkingScheduleString(shop)}
                </S.WorkingHours>
              )}
              {shop?.uri && (
                <S.Link $variant="3" data-testid="author-info-link">
                  {t('pages.ad.users_ads')}
                </S.Link>
              )}
              {usersAdsHref && (
                <S.Link
                  $variant="3"
                  data-testid="author-info-link"
                  as={CustomLink}
                  href={usersAdsHref}
                >
                  {t('pages.ad.users_ads')}
                </S.Link>
              )}
            </div>
            {info.avatar ? (
              <img
                data-testid={AdPageTestIds.authorInfo.avatar}
                src={info.avatar}
                alt={info.name}
                width={61}
                height={61}
                loading="lazy"
              />
            ) : (
              <IconBox
                data-testid={AdPageTestIds.authorInfo.defaultAvatar}
                $filename="profile.svg"
                $width={61}
                $height={61}
              />
            )}
          </S.Main>
          <PhoneNumbers phones={info.phones} adId={id} />
        </>
      )}

      {isWarningVisible && categoryDetails?.warningMessage && (
        <S.Warning data-testid={AdPageTestIds.authorInfo.warning}>
          <IconBox $filename="warning.svg" $width={28} $height={25} />
          <Typography.Body
            $variant="1"
            data-testid={AdPageTestIds.authorInfo.warningText}
          >
            <S.WarningTitle $variant="1" as="span">
              {t('common.attention')}{' '}
            </S.WarningTitle>
            {categoryDetails?.warningMessage}
          </Typography.Body>
        </S.Warning>
      )}

      {isInfoVisible && <Divider $isSmall />}
    </>
  );
};
